import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ARCH_STATUSES, PROJECT_TYPES } from '@shared/types';
import type { AiReview, ArchSubmission, ArchStatus, Attachment, ChecklistItem, KnowledgeCitation, ProjectType } from '@shared/types';
import { ArchNav } from '../components/ArchNav';
import { CitationCard, PERMIT_NOTE } from '../components/KnowledgeAnswer';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsync } from '../lib/useAsync';
import { useToast } from '../lib/toast';
import { dateLabel, money, relativeTime } from '../lib/format';
import { AttachmentGrid, AttachmentUploader } from '../components/Attachments';
import { NoteThread } from '../components/NoteThread';
import { ArchStatusTag, Banner, EmptyState, Field, LoadingPage, Tag } from '../components/ui';
import { PageHeader } from '../components/PageHeader';

const FILTERS: (ArchStatus | 'All' | 'Needs action' | 'Assigned to me')[] = [
  'Needs action',
  'Assigned to me',
  'All',
  'Draft',
  'Submitted',
  'In Review',
  'Info Requested',
  'Approved',
  'Denied',
];

const PENDING: ArchStatus[] = ['Submitted', 'In Review', 'Info Requested'];

export function ArchitecturePage() {
  const { user, isReviewer, isHomeowner } = useAuth();
  const { notify, fail } = useToast();
  const [params] = useSearchParams();
  const openId = params.get('open');
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>(openId ? 'All' : isReviewer ? 'Needs action' : 'All');
  const [showForm, setShowForm] = useState(false);

  const state = useAsync(async () => (await api.archList()).submissions, [user?.id]);
  const guidelines = useAsync(async () => api.guidelines(), []);

  const submissions = state.data ?? [];
  const visible = useMemo(() => {
    if (filter === 'All') return submissions;
    if (filter === 'Needs action') return submissions.filter((s) => PENDING.includes(s.status));
    if (filter === 'Assigned to me') return submissions.filter((s) => user && s.assignedReviewerIds.includes(user.id));
    return submissions.filter((s) => s.status === filter);
  }, [submissions, filter, user]);

  function replace(updated: ArchSubmission) {
    state.setData(submissions.map((item) => (item.id === updated.id ? updated : item)));
  }

  if (state.loading && !state.data) return <LoadingPage label="Loading submissions…" />;

  return (
    <div>
      <PageHeader
        title="Architecture Review"
        lede={
          isReviewer
            ? 'Every request from the community. Research each one against HOA and official sources, then the committee decides. AI output is advisory only.'
            : 'Check a project against the guidelines, then submit it for board approval. Work should not start until you have written approval, and any city permits you need.'
        }
      />
      <ArchNav />

      <div className="row" style={{ marginBottom: 16 }}>
        {isHomeowner && (
          <button type="button" className="btn" onClick={() => setShowForm((open) => !open)}>
            {showForm ? 'Close form' : 'New submission'}
          </button>
        )}
        {guidelines.data && (
          <span className="mut">
            {guidelines.data.guidelines.filter((g) => g.active).length} published ·{' '}
            {guidelines.data.aiEngine === 'gemini' ? 'AI review enabled' : 'rules-based review'}
          </span>
        )}
      </div>

      {showForm && (
        <SubmissionForm
          onCreated={(submission) => {
            state.setData([submission, ...submissions]);
            setShowForm(false);
            notify(
              submission.aiReview
                ? 'Preliminary review is ready. Submit it to the board when you are ready.'
                : 'Draft saved. Run the preliminary review, then submit it to the board.',
              'success',
            );
          }}
          onError={fail}
        />
      )}

      <div className="filters" role="tablist" aria-label="Filter submissions">
        {FILTERS.filter((option) => option !== 'Assigned to me' || isReviewer).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={filter === option}
            className={filter === option ? 'on' : ''}
            onClick={() => setFilter(option)}
          >
            {option}
            {option === 'Needs action' && submissions.filter((s) => PENDING.includes(s.status)).length > 0
              ? ` (${submissions.filter((s) => PENDING.includes(s.status)).length})`
              : ''}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState
          title={filter === 'All' ? 'No submissions yet.' : `Nothing matching "${filter}".`}
          hint={
            isReviewer
              ? 'Submissions from residents will appear here as they come in.'
              : 'Planning a fence, repaint, solar array or addition? Start a submission above.'
          }
        />
      ) : (
        visible.map((submission) => (
          <SubmissionCard key={submission.id} submission={submission} onChange={replace} focus={submission.id === openId} />
        ))
      )}
    </div>
  );
}

/* --------------------------------------------------------------------- form */

function SubmissionForm({
  onCreated,
  onError,
}: {
  onCreated: (submission: ArchSubmission) => void;
  onError: (error: unknown, fallback?: string) => void;
}) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    projectType: 'Other' as ProjectType,
    contractor: '',
    estimatedStart: '',
    estimatedCost: '',
  });
  const [files, setFiles] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const { submission } = await api.archCreate({
        title: form.title,
        description: form.description,
        projectType: form.projectType,
        contractor: form.contractor || undefined,
        estimatedStart: form.estimatedStart || null,
        estimatedCost: form.estimatedCost ? Number(form.estimatedCost) : null,
        attachmentIds: files.map((f) => f.id),
      });
      try {
        const reviewed = await api.archAiReview(submission.id);
        onCreated(reviewed.submission);
      } catch (error) {
        onCreated(submission);
        onError(error, 'The draft was saved, but the preliminary review did not run.');
      }
      setForm({
        title: '',
        description: '',
        projectType: 'Other',
        contractor: '',
        estimatedStart: '',
        estimatedCost: '',
      });
      setFiles([]);
    } catch (error) {
      onError(error, 'Your submission was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h3>New submission</h3>
      <p className="mut">
        The more detail you give, the faster the review. Include dimensions, materials, colors, and
        where on the property the work goes.
      </p>

      <form onSubmit={submit}>
        <Field label="Project title">
          <input
            value={form.title}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
            placeholder="Backyard fence replacement"
            required
            maxLength={160}
          />
        </Field>

        <Field label="Project type">
          <select
            value={form.projectType}
            onChange={(event) => setForm({ ...form, projectType: event.target.value as ProjectType })}
          >
            {PROJECT_TYPES.map((type) => (
              <option key={type}>{type}</option>
            ))}
          </select>
        </Field>

        <Field
          label="Description"
          hint="Dimensions, materials, colors, location on the property, and timing."
        >
          <textarea
            rows={5}
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
            placeholder="6 ft cedar privacy fence along the rear property line, natural stain, finished side facing the greenbelt. Same footprint as the existing fence."
            required
          />
        </Field>

        <div className="grid two">
          <Field label="Contractor" hint="Optional.">
            <input
              value={form.contractor}
              onChange={(event) => setForm({ ...form, contractor: event.target.value })}
            />
          </Field>
          <Field label="Planned start" hint="Optional.">
            <input
              type="date"
              value={form.estimatedStart}
              onChange={(event) => setForm({ ...form, estimatedStart: event.target.value })}
            />
          </Field>
          <Field label="Estimated cost" hint="Optional.">
            <input
              type="number"
              min={0}
              step={100}
              inputMode="decimal"
              value={form.estimatedCost}
              onChange={(event) => setForm({ ...form, estimatedCost: event.target.value })}
            />
          </Field>
        </div>

        <span className="label-text">Photos, drawings and product sheets</span>
        <AttachmentUploader value={files} onChange={setFiles} />

        <div className="btn-row">
          <button type="submit" className="btn" disabled={busy}>
            {busy ? 'Checking…' : 'Save and check guidelines'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* --------------------------------------------------------------------- card */

function SubmissionCard({
  submission,
  onChange,
  focus = false,
}: {
  submission: ArchSubmission;
  onChange: (submission: ArchSubmission) => void;
  /** Opened from a link (e.g. a draft just created from Ask AI). */
  focus?: boolean;
}) {
  const { user, isReviewer, isArb } = useAuth();
  const { notify, fail } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(focus && submission.status === 'Draft');
  const [picked, setPicked] = useState<KnowledgeCitation[]>([]);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focus) cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focus]);
  const [aiBusy, setAiBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [extraFiles, setExtraFiles] = useState<Attachment[]>([]);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [voteBusy, setVoteBusy] = useState(false);

  const isAuthor = submission.authorId === user?.id;

  const canSend = isAuthor && (submission.status === 'Draft' || submission.status === 'Info Requested');
  const myVote = submission.votes.find((vote) => vote.userId === user?.id);

  async function sendToBoard() {
    const missing = submission.checklist.filter((item) => item.required && !item.done);
    const message = [
      missing.length ? `These required checklist items are not ticked: ${missing.map((item) => item.label).join('; ')}.` : '',
      'Submit this request? Its description, photos and documents will be shared with the board and the Architectural Review Committee.',
    ]
      .filter(Boolean)
      .join('\n\n');
    if (!window.confirm(message)) return;
    setSubmitBusy(true);
    try {
      const { submission: updated, mailError } = await api.archSubmit(submission.id);
      onChange(updated);
      notify(
        mailError ? `Submitted, but the board email was not sent. ${mailError}` : 'Submitted. The review board has been notified.',
        mailError ? 'error' : 'success',
      );
    } catch (error) {
      fail(error, 'The request was not submitted.');
    } finally {
      setSubmitBusy(false);
    }
  }

  async function vote(choice: 'approve' | 'deny') {
    setVoteBusy(true);
    try {
      const { submission: updated } = await api.archVote(submission.id, choice, reason.trim() || undefined);
      onChange(updated);
      notify(choice === 'approve' ? 'Approval recorded.' : 'Vote recorded.', 'success');
    } catch (error) {
      fail(error, 'Your vote was not recorded.');
    } finally {
      setVoteBusy(false);
    }
  }

  async function runAiReview() {
    setAiBusy(true);
    try {
      const { submission: updated } = await api.archAiReview(submission.id);
      onChange(updated);
      notify('AI pre-check complete.', 'success');
    } catch (error) {
      fail(error, 'The AI pre-check did not run.');
    } finally {
      setAiBusy(false);
    }
  }

  async function setStatus(status: ArchStatus) {
    const needsReason = status === 'Denied' || status === 'Info Requested';
    if (needsReason && !reason.trim()) {
      notify('Add a short note explaining the decision first.', 'error');
      return;
    }
    setStatusBusy(true);
    try {
      const { submission: updated, mailError } = await api.archSetStatus(
        submission.id,
        status,
        reason.trim() || undefined,
        picked.length ? picked : undefined,
      );
      onChange(updated);
      setReason('');
      setPicked([]);
      notify(
        mailError ? `Marked as ${status}, but the email was not sent. ${mailError}` : `Marked as ${status}.`,
        mailError ? 'error' : 'success',
      );
    } catch (error) {
      fail(error, 'The status was not changed.');
    } finally {
      setStatusBusy(false);
    }
  }

  async function attachMore(items: Attachment[]) {
    setExtraFiles(items);
    if (items.length === 0) return;
    try {
      const { submission: updated } = await api.archAddFiles(
        submission.id,
        items.map((f) => f.id),
      );
      onChange(updated);
      setExtraFiles([]);
      notify('Files added.', 'success');
    } catch (error) {
      fail(error, 'Those files were not attached.');
    }
  }

  return (
    <div className="card" ref={cardRef}>
      <div className="card-head">
        <h3>{submission.title}</h3>
        <div className="tag-row">
          <Tag>{submission.projectType}</Tag>
          <ArchStatusTag status={submission.status} />
        </div>
      </div>

      <div className="mut">
        {submission.reference} · {submission.authorName}
        {submission.authorUnit ? ` · ${submission.authorUnit}` : ''} ·{' '}
        {submission.status === 'Draft' ? 'started' : 'submitted'} {relativeTime(submission.createdAt)}
      </div>

      {editing && isAuthor ? (
        <DraftEditor submission={submission} onSaved={(next) => {
          onChange(next);
          setEditing(false);
        }} onCancel={() => setEditing(false)} />
      ) : (
        <p className="pre-wrap">{submission.description}</p>
      )}

      {(submission.contractor || submission.estimatedStart || submission.estimatedCost !== null) && (
        <div className="mut">
          {submission.contractor && <>Contractor: {submission.contractor}. </>}
          {submission.estimatedStart && <>Planned start: {dateLabel(submission.estimatedStart)}. </>}
          {submission.estimatedCost !== null && <>Estimated cost: {money(submission.estimatedCost)}.</>}
        </div>
      )}

      <AttachmentGrid items={submission.attachments} />

      {submission.checklist.length > 0 && (
        <Checklist submission={submission} editable={isAuthor && canSend} onChange={onChange} />
      )}

      {submission.sourceRefs.length > 0 && (
        <details className="kc-drawer">
          <summary>{submission.sourceRefs.length} referenced resource{submission.sourceRefs.length === 1 ? '' : 's'}</summary>
          <ul>
            {submission.sourceRefs.map((ref) => (
              <li key={ref.sourceId}>
                {ref.url ? (
                  <a href={ref.url} target={ref.url.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">
                    {ref.title}
                  </a>
                ) : (
                  ref.title
                )}
                {ref.excerpt ? <span className="mut"> — “{ref.excerpt}”</span> : null}
              </li>
            ))}
          </ul>
        </details>
      )}

      {submission.decisionReason && (
        <Banner kind={submission.status === 'Denied' ? 'error' : 'info'}>
          <strong>{submission.status}</strong>
          {submission.decidedBy ? ` by ${submission.decidedBy}` : ''}: {submission.decisionReason}
        </Banner>
      )}
      {submission.decisionCitations.length > 0 && (
        <details className="kc-drawer">
          <summary>Sources cited with this decision</summary>
          <ul className="kc-cites">
            {submission.decisionCitations.map((citation, index) => (
              <CitationCard key={`${citation.sourceId}-${index}`} citation={citation} />
            ))}
          </ul>
        </details>
      )}
      {(submission.status === 'Approved' || canSend) && <p className="mut kc-note">{PERMIT_NOTE}</p>}

      {submission.aiReview && <AiReviewPanel review={submission.aiReview} />}

      {submission.votes.length > 0 && (
        <div className="mut">
          Votes: {submission.votes.filter((item) => item.vote === 'approve').length} approve,{' '}
          {submission.votes.filter((item) => item.vote === 'deny').length} deny
          {submission.arbMemberCount > 0 ? ` · ${submission.arbMemberCount} board members` : ''}.
          {submission.votes.map((item) => ` ${item.name} ${item.vote === 'approve' ? 'approved' : 'denied'}.`).join('')}
        </div>
      )}

      <div className="btn-row">
        <button type="button" className="btn s tiny" onClick={() => setExpanded((open) => !open)}>
          {expanded
            ? 'Hide discussion'
            : `Discussion${submission.notes.length > 0 ? ` (${submission.notes.length})` : ''}`}
        </button>

        {(isAuthor || isReviewer) && (
          <button type="button" className="btn s tiny" disabled={aiBusy} onClick={() => void runAiReview()}>
            {aiBusy ? 'Checking…' : submission.aiReview ? 'Re-run preliminary review' : 'Preliminary review'}
          </button>
        )}

        {canSend && !editing && (
          <button type="button" className="btn s tiny" onClick={() => setEditing(true)}>
            Edit details
          </button>
        )}

        {canSend && (
          <button type="button" className="btn tiny" disabled={submitBusy} onClick={() => void sendToBoard()}>
            {submitBusy ? 'Sending…' : 'Submit to the board'}
          </button>
        )}
      </div>

      {isReviewer && submission.status !== 'Draft' && (
        <CommitteePanel submission={submission} onChange={onChange} picked={picked} onPick={setPicked} />
      )}

      {isArb && submission.status !== 'Draft' && submission.status !== 'Withdrawn' && (
        <div style={{ marginTop: 14 }}>
          <Field label="Vote note" hint="Optional. Shown with your sign-off.">
            <textarea
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Looks consistent with the fence guidelines."
            />
          </Field>
          <div className="btn-row" style={{ marginTop: 0 }}>
            <button type="button" className="btn tiny" disabled={voteBusy} onClick={() => void vote('approve')}>
              {myVote?.vote === 'approve' ? 'Approval recorded' : 'Approve and sign off'}
            </button>
            <button type="button" className="btn s tiny" disabled={voteBusy} onClick={() => void vote('deny')}>
              {myVote?.vote === 'deny' ? 'Denial recorded' : 'Vote no'}
            </button>
          </div>
        </div>
      )}

      {isReviewer && (
        <div style={{ marginTop: 14 }}>
          {picked.length > 0 && (
            <p className="mut">
              {picked.length} cited source{picked.length === 1 ? '' : 's'} will be attached to this decision.
            </p>
          )}
          <Field label="Decision note" hint="Required when denying or sending the request back. Your decision, not the AI’s.">
            <textarea
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Please add the fence height and stain color, then resubmit."
            />
          </Field>
          <div className="btn-row" style={{ marginTop: 0 }}>
            <button
              type="button"
              className="btn s tiny"
              disabled={statusBusy || submission.status === 'Info Requested'}
              onClick={() => void setStatus('Info Requested')}
            >
              Send back for resubmission
            </button>
            {ARCH_STATUSES.filter(
              (status) =>
                status !== submission.status &&
                status !== 'Withdrawn' &&
                status !== 'Draft' &&
                status !== 'Info Requested',
            ).map((status) => (
              <button
                key={status}
                type="button"
                className="btn s tiny"
                disabled={statusBusy}
                onClick={() => void setStatus(status)}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      )}

      {isAuthor && submission.status !== 'Approved' && submission.status !== 'Denied' && (
        <div style={{ marginTop: 12 }}>
          <span className="label-text">Add more photos or documents</span>
          <AttachmentUploader value={extraFiles} onChange={(items) => void attachMore(items)} max={5} />
        </div>
      )}

      {expanded && (
        <NoteThread
          notes={submission.notes}
          allowInternal={isReviewer}
          placeholder={isReviewer ? 'Note for the homeowner…' : 'Reply or add information…'}
          onSubmit={async (body, internal, attachmentIds) => {
            const { submission: updated } = await api.archAddNote(
              submission.id,
              body,
              internal,
              attachmentIds,
              isReviewer && picked.length ? picked : undefined,
            );
            onChange(updated);
            if (isReviewer) setPicked([]);
          }}
        />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- ai review */

const VERDICT_COPY: Record<AiReview['verdict'], { label: string; tone: 'ok' | 'warn' | 'bad' | 'info' }> = {
  likely_compliant: { label: 'Looks consistent with the guidelines', tone: 'ok' },
  needs_attention: { label: 'Needs a closer look', tone: 'warn' },
  likely_non_compliant: { label: 'Appears to conflict with a guideline', tone: 'bad' },
  unknown: { label: 'No guideline matched', tone: 'info' },
};

function AiReviewPanel({ review }: { review: AiReview }) {
  const verdict = VERDICT_COPY[review.verdict];

  return (
    <div className="card tight" style={{ marginTop: 14, marginBottom: 0 }}>
      <div className="card-head">
        <h4>AI pre-check</h4>
        <div className="tag-row">
          <Tag tone={verdict.tone}>{verdict.label}</Tag>
          <Tag>{review.engine === 'gemini' ? (review.model ?? 'AI review') : 'Rules engine'}</Tag>
        </div>
      </div>

      <p style={{ fontSize: 14 }}>{review.summary}</p>

      {review.findings.length > 0 && (
        <div className="stack" style={{ gap: 10, marginTop: 10 }}>
          {review.findings.map((finding, index) => (
            <div key={index}>
              <div className="row" style={{ gap: 8 }}>
                <strong style={{ fontSize: 14 }}>{finding.guidelineTitle}</strong>
                <Tag
                  tone={
                    finding.severity === 'blocker' ? 'bad' : finding.severity === 'warning' ? 'warn' : 'info'
                  }
                >
                  {finding.severity}
                </Tag>
              </div>
              <div className="mut">{finding.requirement}</div>
              <div style={{ fontSize: 14 }}>{finding.assessment}</div>
            </div>
          ))}
        </div>
      )}

      {review.missingInfo.length > 0 && (
        <>
          <div className="label-text" style={{ marginTop: 12 }}>
            Not stated in the submission
          </div>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20, fontSize: 14 }}>
            {review.missingInfo.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </>
      )}

      <p className="mut" style={{ marginTop: 12, marginBottom: 0 }}>
        Advisory only — generated {relativeTime(review.createdAt)}. The board makes the decision.
      </p>
    </div>
  );
}

/* ----------------------------------------------------------------- drafts */

function DraftEditor({
  submission,
  onSaved,
  onCancel,
}: {
  submission: ArchSubmission;
  onSaved: (submission: ArchSubmission) => void;
  onCancel: () => void;
}) {
  const { fail, notify } = useToast();
  const [form, setForm] = useState({
    title: submission.title,
    description: submission.description,
    projectType: submission.projectType,
    contractor: submission.contractor ?? '',
    estimatedStart: submission.estimatedStart ?? '',
    estimatedCost: submission.estimatedCost === null ? '' : String(submission.estimatedCost),
  });
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      const { submission: updated } = await api.archUpdate(submission.id, {
        title: form.title.trim(),
        description: form.description.trim(),
        projectType: form.projectType,
        contractor: form.contractor.trim() || null,
        estimatedStart: form.estimatedStart || null,
        estimatedCost: form.estimatedCost ? Number(form.estimatedCost) : null,
      });
      onSaved(updated);
      notify('Draft saved.', 'success');
    } catch (error) {
      fail(error, 'The draft was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <Field label="Title">
        <input value={form.title} maxLength={160} onChange={(e) => setForm({ ...form, title: e.target.value })} />
      </Field>
      <Field label="Project type">
        <select value={form.projectType} onChange={(e) => setForm({ ...form, projectType: e.target.value as ProjectType })}>
          {PROJECT_TYPES.map((type) => (
            <option key={type}>{type}</option>
          ))}
        </select>
      </Field>
      <Field label="Description" hint="Sizes, materials, colors and where on the lot.">
        <textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </Field>
      <div className="kc-form-grid">
        <Field label="Contractor">
          <input value={form.contractor} onChange={(e) => setForm({ ...form, contractor: e.target.value })} />
        </Field>
        <Field label="Planned start">
          <input type="date" value={form.estimatedStart} onChange={(e) => setForm({ ...form, estimatedStart: e.target.value })} />
        </Field>
        <Field label="Estimated cost">
          <input type="number" min={0} value={form.estimatedCost} onChange={(e) => setForm({ ...form, estimatedCost: e.target.value })} />
        </Field>
      </div>
      <p className="mut">Add photos, plans and a site plan below. Nothing is shared with the committee until you submit.</p>
      <div className="btn-row">
        <button type="button" className="btn tiny" disabled={busy || !form.title.trim() || !form.description.trim()} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save draft'}
        </button>
        <button type="button" className="btn s tiny" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function Checklist({
  submission,
  editable,
  onChange,
}: {
  submission: ArchSubmission;
  editable: boolean;
  onChange: (submission: ArchSubmission) => void;
}) {
  const { fail } = useToast();
  async function toggle(item: ChecklistItem, done: boolean) {
    try {
      const checklist = submission.checklist.map((other) => (other.id === item.id ? { ...other, done } : other));
      const { submission: updated } = await api.archUpdate(submission.id, { checklist });
      onChange(updated);
    } catch (error) {
      fail(error, 'The checklist was not saved.');
    }
  }
  const done = submission.checklist.filter((item) => item.done).length;
  return (
    <div className="kc-research">
      <h4>
        Before you submit · {done}/{submission.checklist.length}
      </h4>
      <ul className="kc-checklist">
        {submission.checklist.map((item) => (
          <li key={item.id}>
            <label>
              <input type="checkbox" checked={Boolean(item.done)} disabled={!editable} onChange={(e) => void toggle(item, e.target.checked)} />
              <span>
                {item.label} {item.required ? <Tag tone="warn">Required by the board</Tag> : null}
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------- committee */

function CommitteePanel({
  submission,
  onChange,
  picked,
  onPick,
}: {
  submission: ArchSubmission;
  onChange: (submission: ArchSubmission) => void;
  picked: KnowledgeCitation[];
  onPick: (citations: KnowledgeCitation[]) => void;
}) {
  const { notify, fail } = useToast();
  const reviewers = useAsync(() => api.archReviewers(), []);
  const [busy, setBusy] = useState<'research' | 'assign' | 'save' | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const latest = submission.research[0] ?? null;

  async function research() {
    setBusy('research');
    try {
      const { submission: updated } = await api.archResearch(submission.id);
      onChange(updated);
      notify('Research summary drafted. Check every finding before relying on it.', 'success');
    } catch (error) {
      fail(error, 'Research did not run.');
    } finally {
      setBusy(null);
    }
  }

  async function assign(ids: string[]) {
    setBusy('assign');
    try {
      const { submission: updated } = await api.archAssign(submission.id, ids);
      onChange(updated);
    } catch (error) {
      fail(error, 'Reviewers were not assigned.');
    } finally {
      setBusy(null);
    }
  }

  async function saveEdit(noteId: string) {
    setBusy('save');
    try {
      const { submission: updated } = await api.archEditResearch(submission.id, noteId, draft);
      onChange(updated);
      setEditingId(null);
      notify('Research note saved.', 'success');
    } catch (error) {
      fail(error, 'The research note was not saved.');
    } finally {
      setBusy(null);
    }
  }

  const togglePick = (citation: KnowledgeCitation, on: boolean) =>
    onPick(on ? [...picked, citation] : picked.filter((item) => !(item.sourceId === citation.sourceId && item.excerpt === citation.excerpt)));

  return (
    <div className="kc-research" aria-label="Committee workspace">
      <h4>Committee workspace</h4>
      {reviewers.data && (
        <fieldset className="kc-check-group" disabled={busy === 'assign'}>
          <legend className="label-text">Assigned reviewers</legend>
          {reviewers.data.reviewers.map((reviewer) => (
            <label key={reviewer.id} className="kc-check">
              <input
                type="checkbox"
                checked={submission.assignedReviewerIds.includes(reviewer.id)}
                onChange={(e) =>
                  void assign(
                    e.target.checked
                      ? [...submission.assignedReviewerIds, reviewer.id]
                      : submission.assignedReviewerIds.filter((id) => id !== reviewer.id),
                  )
                }
              />
              <span>{reviewer.name}</span>
            </label>
          ))}
        </fieldset>
      )}

      <div className="btn-row">
        <button type="button" className="btn s tiny" disabled={busy !== null} onClick={() => void research()}>
          {busy === 'research' ? 'Researching…' : latest ? 'Research again with AI' : 'Research with AI'}
        </button>
      </div>
      <p className="mut kc-note">
        Advisory only. AI research searches published sources and committee-only documents; it never approves or denies. The committee verifies every finding.
      </p>

      {latest && (
        <div>
          <div className="kc-section-head">
            <strong>
              {latest.aiGenerated ? 'AI research draft' : 'Research note'} · {relativeTime(latest.createdAt)}
              {latest.authorName ? ` · ${latest.authorName}` : ''}
            </strong>
            <Tag tone="info">Advisory</Tag>
          </div>
          {editingId === latest.id ? (
            <>
              <textarea rows={10} value={draft} onChange={(e) => setDraft(e.target.value)} aria-label="Edit research summary" />
              <div className="btn-row">
                <button type="button" className="btn tiny" disabled={busy !== null} onClick={() => void saveEdit(latest.id)}>
                  Save
                </button>
                <button type="button" className="btn s tiny" onClick={() => setEditingId(null)}>
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="pre-wrap">{latest.body}</p>
              <button
                type="button"
                className="btn ghost tiny"
                onClick={() => {
                  setDraft(latest.body);
                  setEditingId(latest.id);
                }}
              >
                Edit summary
              </button>
            </>
          )}
          {latest.citations.length > 0 && (
            <details className="kc-drawer" open>
              <summary>{latest.citations.length} citations · tick to attach to your next note or decision</summary>
              <ul className="kc-cites">
                {latest.citations.map((citation, index) => (
                  <li key={`${citation.sourceId}-${index}`} style={{ listStyle: 'none' }}>
                    <label className="kc-check">
                      <input
                        type="checkbox"
                        checked={picked.some((item) => item.sourceId === citation.sourceId && item.excerpt === citation.excerpt)}
                        onChange={(e) => togglePick(citation, e.target.checked)}
                      />
                      <span>Attach</span>
                    </label>
                    <CitationCard citation={citation} as="div" />
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
